import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import DashboardPage from "@/pages/DashboardPage";
import IncidentsPage from "@/pages/IncidentsPage";
import ClientsPage from "@/pages/ClientsPage";
import ContactsPage from "@/pages/ContactsPage";
import OpsConsolePage from "@/pages/OpsConsolePage";
import CommunicationsPage from "@/pages/CommunicationsPage";
import ReportsPage from "@/pages/ReportsPage";
import CommsHubPage from "@/pages/commsHub/CommsHubPage";
import MailHistoryPage from "@/pages/MailHistoryPage";
import CrewPage from "@/pages/CrewPage";
import SettingsPage from "@/pages/SettingsPage";
import ClientSignupsPage from "@/pages/ClientSignupsPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import ClientSignup from "@/pages/ClientSignup";
import LoginPage from "@/pages/LoginPage";
import PortalPage from "@/pages/PortalPage";
import ActivatePage from "@/pages/ActivatePage";
import HeroPreviewPage from "@/pages/HeroPreviewPage";
import MarketingPage from "@/pages/MarketingPage";
import ConsumerApp from "@/pages/consumer/ConsumerApp";
import PrototypeHub from "@/pages/wireframe/PrototypeHub";
import AdminWireframe from "@/pages/wireframe/AdminWireframe";
import MarketingWireframe from "@/pages/wireframe/MarketingWireframe";
import { TermsPage, PrivacyPage, PopiaPage } from "@/pages/LegalPage";
import ResetPasswordPage from "@/pages/consumer/ResetPasswordPage";
import FleetOnboard from "@/pages/consumer/FleetOnboard";
import DebitOrderPage from "@/pages/DebitOrderPage";
import UpdatePrompt from "@/components/UpdatePrompt";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] text-[#5B6B7E] font-mono-data text-xs tracking-widest">
    AUTHENTICATING...
  </div>
);

// Any signed-in account that belongs to neither console lands here. Never redirect such a user —
// bouncing them between /admin and /portal is what caused the blank-screen redirect loop.
function NoConsoleAccess() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F7F5F0]" data-testid="no-console-access">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-[0_18px_50px_rgba(11,30,59,0.10)]">
        <div className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-[#E01A2E] font-bold">ResQ1</div>
        <h1 className="text-2xl mt-3 mb-2 text-[#1A1E24]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
          This is a member account.
        </h1>
        <p className="text-sm text-[#5B6B7E] mb-7">
          {user?.email} is a ResQ1 member, not a dispatch operator, so it can't open the ops console.
          Use the ResQ1 app for cover, family and panic — or sign in with your operator account.
        </p>
        <div className="flex flex-wrap gap-3">
          <a href="/app" data-testid="open-consumer-app-btn"
            className="bg-[#E01A2E] text-white text-sm font-bold rounded-full px-6 py-3 no-underline">
            Open the ResQ1 app
          </a>
          <button onClick={logout} data-testid="no-access-signout-btn"
            className="text-sm font-bold rounded-full px-6 py-3 border border-[#1A1E24]/20 text-[#1A1E24]">
            Sign in as operator
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminLayout({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  if (user.role !== "admin") return <NoConsoleAccess />;
  return (
    <div className="admin-theme flex min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

function ClientRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role !== "client") return <NoConsoleAccess />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/hero-preview" element={<HeroPreviewPage />} />
          <Route path="/activate/:token" element={<ActivatePage />} />
          <Route path="/portal" element={<ClientRoute><PortalPage /></ClientRoute>} />
          <Route path="/" element={<MarketingPage />} />
          <Route path="/app" element={<ConsumerApp />} />
          <Route path="/fleet/:token" element={<FleetOnboard />} />
          <Route path="/prototype" element={<PrototypeHub />} />
          <Route path="/prototype/admin" element={<AdminWireframe />} />
          <Route path="/prototype/marketing" element={<MarketingWireframe />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/popia" element={<PopiaPage />} />
          <Route path="/reset" element={<ResetPasswordPage />} />
          <Route path="/signup/:token" element={<ClientSignup />} />
          <Route path="/debit-order/:token" element={<DebitOrderPage />} />
          <Route path="/admin" element={<AdminLayout><DashboardPage /></AdminLayout>} />
          <Route path="/incidents" element={<AdminLayout><IncidentsPage /></AdminLayout>} />
          <Route path="/intake" element={<Navigate to="/settings" replace />} />
          <Route path="/clients" element={<AdminLayout><ClientsPage /></AdminLayout>} />
          <Route path="/client-signups" element={<AdminLayout><ClientSignupsPage /></AdminLayout>} />
          <Route path="/contacts" element={<AdminLayout><ContactsPage /></AdminLayout>} />
          <Route path="/subscriptions" element={<AdminLayout><SubscriptionsPage /></AdminLayout>} />
          <Route path="/ops" element={<AdminLayout><OpsConsolePage /></AdminLayout>} />
          <Route path="/communications" element={<AdminLayout><CommunicationsPage /></AdminLayout>} />
          <Route path="/reports" element={<AdminLayout><ReportsPage /></AdminLayout>} />
          <Route path="/comms-hub" element={<AdminLayout><CommsHubPage /></AdminLayout>} />
          <Route path="/mail-history" element={<AdminLayout><MailHistoryPage /></AdminLayout>} />
          <Route path="/crew" element={<AdminLayout><CrewPage /></AdminLayout>} />
          <Route path="/settings" element={<AdminLayout><SettingsPage /></AdminLayout>} />
          <Route path="/subscribers" element={<Navigate to="/contacts" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-right" richColors />
        <UpdatePrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
